import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLogin from './components/AdminLogin';
import AdminSidebar, { type AdminTab } from './components/AdminSidebar';
import OverviewTab from './components/OverviewTab';
import ClientsTab from './components/ClientsTab';
import RequestsTab from './components/RequestsTab';
import CodesTab from './components/CodesTab';
import BanksTab from './components/BanksTab';
import SettingsTab from './components/SettingsTab';
import IpsTab from './components/IpsTab';
import LogsTab from './components/LogsTab';
import ManagementTab from './components/ManagementTab';
import { getAdminSession, adminLogout, type AdminSession } from '@/hooks/useAdminAuth';
import { useAdminData } from '@/hooks/useAdminData';

export default function AdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [session, setSession] = useState<AdminSession | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [mobileOpen, setMobileOpen] = useState(false);

  const data = useAdminData();

  useEffect(() => {
    const current = getAdminSession();
    if (current) setSession(current);
  }, []);

  const handleLogout = () => {
    adminLogout();
    setSession(null);
    setActiveTab('overview');
  };

  const handleBackToSite = () => {
    navigate('/');
  };

  const handleEmailChanged = (email: string) => {
    setSession((prev) => (prev ? { ...prev, email } : prev));
  };

  if (!session) {
    return <AdminLogin onSuccess={() => setSession(getAdminSession())} />;
  }

  if (data.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 text-sm text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin text-lg"></i>
        {t('admin.loading')}
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background-50 px-4 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-900">
          <i className="ri-error-warning-line text-xl"></i>
        </span>
        <p className="text-sm text-foreground-600">{data.error}</p>
        <button
          type="button"
          onClick={() => void data.reload()}
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          <i className="ri-refresh-line"></i>
          {t('admin.retry')}
        </button>
      </div>
    );
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'clients':
        return (
          <ClientsTab
            clients={data.clients}
            managerEmail={session.email}
            onAddClient={data.addClient}
            onToggleStatus={data.toggleClientStatus}
            onSaveCredentials={data.saveClientCredentials}
            onSendCredentials={data.sendClientCredentials}
          />
        );
      case 'requests':
        return (
          <RequestsTab
            requests={data.requests}
            managerEmail={session.email}
            onActivate={data.activateRequest}
          />
        );
      case 'codes':
        return (
          <CodesTab
            requests={data.requests}
            managerEmail={session.email}
            onRenew={data.renewCode}
            onInvalidate={data.invalidateCode}
          />
        );
      case 'banks':
        return (
          <BanksTab
            banks={data.banks}
            managerEmail={session.email}
            onAddBank={data.addBank}
            onDeleteBank={data.deleteBank}
          />
        );
      case 'settings':
        return (
          <SettingsTab
            adminEmail={session.email}
            codeLink={data.codeLink}
            onSaveCodeLink={data.saveCodeLink}
            onEmailChanged={handleEmailChanged}
          />
        );
      case 'management':
        return (
          <ManagementTab
            clients={data.clients}
            requests={data.requests}
            blockedIps={data.blockedIps}
            managerEmail={session.email}
            onDeleteClient={data.deleteClient}
            onPurgeAllClients={data.purgeAllClients}
            onDeleteRequest={data.deleteRequest}
            onPurgeAllRequests={data.purgeAllRequests}
            onDeleteIp={data.unblockIp}
            onPurgeBlockedIps={data.purgeBlockedIps}
          />
        );
      case 'ips':
        return (
          <IpsTab
            blockedIps={data.blockedIps}
            managerEmail={session.email}
            onBlock={data.blockIp}
            onUnblock={data.unblockIp}
          />
        );
      case 'logs':
        return <LogsTab logs={data.logs} />;
      case 'overview':
      default:
        return (
          <OverviewTab
            stats={data.stats}
            requests={data.requests}
            onViewRequests={() => setActiveTab('requests')}
          />
        );
    }
  };

  return (
    <div className="flex min-h-screen bg-background-50">
      <div className="hidden shrink-0 border-r border-background-200 lg:block">
        <AdminSidebar activeTab={activeTab} onSelect={setActiveTab} />
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setMobileOpen(false)}
          ></div>
          <div className="absolute left-0 top-0 h-full w-64 bg-background-50">
            <AdminSidebar
              activeTab={activeTab}
              onSelect={(tab) => {
                setActiveTab(tab);
                setMobileOpen(false);
              }}
            />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex w-full items-center justify-between gap-3 border-b border-background-200 bg-background-50/95 px-4 py-3 backdrop-blur md:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
              aria-label="Menu"
            >
              <i className="ri-menu-line text-lg"></i>
            </button>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1.5 text-xs font-semibold text-primary-900">
              <i className="ri-shield-keyhole-line"></i>
              {t('admin.header.role')}
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={handleBackToSite}
              className="hidden cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 sm:flex"
            >
              <i className="ri-external-link-line"></i>
              {t('admin.header.viewSite')}
            </button>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-semibold text-secondary-900">
                {session.name.charAt(0)}
              </span>
              <span className="hidden text-sm font-semibold text-foreground-800 md:inline">
                {session.name}
              </span>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-logout-box-r-line"></i>
              <span className="hidden sm:inline">{t('admin.header.logout')}</span>
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 md:px-6 md:py-8">
          <div className="mx-auto w-full max-w-[1200px] animate-fade-rise">{renderTab()}</div>
        </main>
      </div>
    </div>
  );
}